import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Interaction } from '@redpanda/types';
import { useInteractionStore } from '../stores/interactionStore';
import { RowActions, RowAction } from '@redpanda/ui';
import { formatTableDate } from '@redpanda/utils';
import { canModify } from '../utils/permissions';

interface InteractionListProps {
  customerId: string;
  refreshKey: number; // Add a key to trigger re-fetch
  onEdit: (interaction: Interaction) => void;
  onDelete: (interactionId: string) => void;
  currentUserId?: string;
  currentUserRole?: string;
}

const InteractionList: React.FC<InteractionListProps> = ({ customerId, refreshKey, onEdit, onDelete, currentUserId, currentUserRole }) => {
  const { t, i18n } = useTranslation();
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { 
    fetchInteractions, 
    getInteractionsFromCache 
  } = useInteractionStore();

  useEffect(() => {
    if (!customerId) return;

    // 首先嘗試從快取中取得資料
    const cachedInteractions = getInteractionsFromCache(customerId);
    
    if (cachedInteractions) {
      // 如果有快取資料，立即使用（快速載入）
      setInteractions(cachedInteractions);
      setIsLoading(false);
      setError(null);
    } else {
      // 如果沒有快取，發送 API 請求
      const loadInteractions = async () => {
        setIsLoading(true);
        setError(null);
        try {
          await fetchInteractions(customerId);
          const updatedInteractions = getInteractionsFromCache(customerId);
          setInteractions(updatedInteractions || []);
        } catch (err: any) {
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };

      loadInteractions();
    }
  }, [customerId, refreshKey, fetchInteractions, getInteractionsFromCache]);

  const renderContent = () => {
    if (isLoading) {
      return <p className="px-5 py-4 text-sm text-fg-secondary">{t('common.status.loading')}</p>;
    }

    if (error) {
      return <p className="px-5 py-4 text-sm text-red-600 dark:text-red-400">{t('errors.business.fetchInteractions')}: {error}</p>;
    }

    if (interactions.length === 0) {
      return <p className="px-5 py-4 text-sm text-fg-secondary">{t('components.interactions.none')}</p>;
    }

    return (
      // 單一容器＋細線分隔（R13）；類型不再用品牌色大字
      <ul className="divide-y divide-line">
        {interactions.map((interaction) => (
          <li key={interaction.id} className="px-5 py-3.5">
            <div className="flex justify-between items-start gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-fg">{interaction.type}</p>
                <p className="text-sm text-fg-secondary mt-0.5 whitespace-pre-wrap">{interaction.content}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[13px] text-fg-secondary tabular-nums">
                  {formatTableDate(interaction.date, i18n.language)}
                  {interaction.createdByName && <span className="ml-2">{interaction.createdByName}</span>}
                </p>
                {canModify(interaction, currentUserRole, currentUserId) && (
                  <RowActions className="mt-1.5 justify-end">
                    <RowAction onClick={() => { window.scrollTo(0, 0); onEdit(interaction); }}>{t('common.actions.edit')}</RowAction>
                    <RowAction tone="danger" onClick={() => onDelete(interaction.id)}>{t('common.actions.delete')}</RowAction>
                  </RowActions>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <section className="bg-surface rounded-lg border border-line">
      <h3 className="px-5 pt-4 pb-3 text-base font-semibold text-fg border-b border-line">{t('components.interactions.title')}</h3>
      {renderContent()}
    </section>
  );
};

export default InteractionList;
